import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { COPY } from "../shared/copy";
import { getLeagueState, getLeaderboardWithMovement, type Member } from "../lib/league";
import { Avatar } from "../components/Avatar";
import { rankBadge } from "../shared/format";
import { Movement } from "./Home";

// Full leaderboard. Ranked by points; ties ordered by average correct response
// time, then total. Speed is NEVER points. A small avg is shown only to explain a
// tie. If a league stake is configured, show an informational note only.
export function Leaderboard() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState<any[] | null>(null);
  const [failed, setFailed] = useState(false);

  async function load() {
    setFailed(false);
    try {
      const { season, members } = await getLeagueState(leagueId!, user!.id);
      setBoard(season ? await getLeaderboardWithMovement(season.id, members as Member[]) : []);
    } catch {
      setFailed(true);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leagueId, user]);

  // Show the avg-time hint only on rows that tie another row on points.
  const pointCounts = new Map<number, number>();
  for (const e of board ?? []) pointCounts.set(e.points, (pointCounts.get(e.points) ?? 0) + 1);
  // Nobody has played a single question yet, so this is a roster, not a ranking.
  const noResultsYet = !!board && board.every((e) => (e.attempted_count ?? 0) === 0);

  if (failed) {
    return (
      <div className="app">
        <div className="header">
          <h1>Leaderboard</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card">
          <p className="error">We couldn&apos;t load the standings.</p>
          <p className="muted">That is usually the connection. Try again.</p>
          <button className="btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <h1>Leaderboard</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>
      <p className="muted">
        {COPY.leaderboard.scoring}
        {board !== null && board.some((e) => e.movement != null && e.movement !== 0) && " Arrows show how far you moved since the last drop."}
      </p>

      {board === null && <div className="loading">Loading the standings…</div>}

      {noResultsYet && (
        <div className="card live">
          <span className="tag">Nothing to rank yet</span>
          <p>No drop has finished, so everybody is sitting on zero. Here&apos;s who&apos;s in.</p>
        </div>
      )}

      {board !== null && board.length > 0 && (
      <div className="card">
        {board.map((e) => {
          const tied = (pointCounts.get(e.points) ?? 0) > 1 && e.avg_correct_response_ms != null;
          return (
            <div className={`lb-row ${e.user_id === user!.id ? "me" : ""}`} key={e.user_id}>
              <span className="lb-rank">{rankBadge(e.rank, e.points)}</span>
              <Avatar id={e.user_id} name={e.name} />
              <span className="lb-name">
                {e.name}{e.user_id === user!.id ? " (you)" : ""}
                {tied && <span className="muted"> · {(e.avg_correct_response_ms / 1000).toFixed(1)}s avg</span>}
                {/* Same rule as the home preview: somebody who has not played a
                    question yet gets no record line rather than "0/0 correct". */}
                {e.attempted_count > 0 && (
                  <>
                    <br />
                    <span className="muted lb-record">{e.correct_count}/{e.attempted_count} correct</span>
                  </>
                )}
              </span>
              <Movement value={e.movement} />
              <span className="lb-points">{e.points}</span>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
